import { toast } from "react-toastify";
import { useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { login } from "../slices/TokensSlice";

/**
 * Custom hook to make API calls with token refresh logic.
 *
 * @param {string} [baseUrl=process.env.REACT_APP_CONFIGURATOR_API_URL] - The base URL for the API.
 * @returns {Object} - An object containing the makeRequest function and isLoading state.
 *
 * @typedef {Object} ApiOptions
 * @property {string} [method] - The HTTP method to use for the request.
 * @property {Object} [headers] - Additional headers to include in the request.
 * @property {Object} [body] - The body of the request.
 * @property {Object} [authTokens] - The authentication tokens to use for the request.
 * @property {string} [authTokens.user_token] - The user token for authentication.
 * @property {string} [authTokens.refresh_token] - The refresh token for authentication.
 * @property {boolean} [ignore_error=false] - When true, suppresses the automatic generic
 *   error toast on failure. The error is still logged and rethrown.
 *
 * @function makeRequest
 * @param {string} urlPart - The URL part to append to the base URL.
 * @param {ApiOptions} [apiOptions={}] - The options for the API request.
 * @returns {Promise<Object>} - The response data from the API call.
 *
 * @function refreshTokens
 * @param {Object} tokens - The new tokens to refresh.
 * @param {string} tokens.user_token - The new user token.
 * @param {string} tokens.refresh_token - The new refresh token.
 */

// eslint-disable-next-line no-undef
const useApiCall = (baseUrl = process.env.REACT_APP_CONFIGURATOR_API_URL) => {
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  // Move token refresh logic here
  const refreshTokens = useCallback(
    (tokens) => {
      dispatch(login(tokens));
    },
    [dispatch]
  );

  const makeRequest = useCallback(
    async (urlPart, apiOptions = {}) => {
      setIsLoading(true);

      try {
        const headers = apiOptions.headers ? { ...apiOptions.headers } : {};

        if (apiOptions.authTokens) {
          headers.Authorization = `Bearer ${apiOptions.authTokens?.user_token}`;
        }

        if (!apiOptions.body || !(apiOptions.body instanceof FormData)) {
          if (!headers["Content-Type"]) {
            headers["Content-Type"] = "application/json";
          }
        }

        const options = {
          method: apiOptions.method,
          headers,
        };

        if (apiOptions?.body) {
          options.body =
            apiOptions.body instanceof FormData
              ? apiOptions.body
              : JSON.stringify(apiOptions.body);
        }

        const url = `${baseUrl}${urlPart}`;
        const response = await fetch(url, options);

        if (response.ok) {
          const data = await response.json();
          return data;
        }

        if (response.status === 401 && apiOptions?.authTokens && !apiOptions?._isRetry) {
          const tokens = await makeRequest("/v1/users/generate_access_token", {
            method: "POST",
            body: { refresh_token: apiOptions.authTokens.refresh_token },
          });

          if (!tokens) {
            localStorage.clear();
            window.location.reload();
            return;
          }

          // Handle token refresh here instead of callback
          refreshTokens({
            refresh_token: apiOptions.authTokens.refresh_token,
            ...tokens,
          });

          // Retry the original request with new tokens
          const res = await makeRequest(urlPart, {
            ...apiOptions,
            authTokens: tokens,
            _isRetry: true,
          });

          return res;
        }

        const errorBody = await response.json().catch(() => null);
        const errorMessage =
          errorBody?.error || errorBody?.message || `Request failed (${response.status})`;
        const err = new Error(errorMessage);
        err.response = errorBody;
        throw err;
      } catch (error) {
        console.error("Error during API call:", error);
        if (!apiOptions.ignore_error) {
          toast.error(error.message || "Something went wrong. Please refresh page and try again.");
        }
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [baseUrl, refreshTokens]
  );

  return { makeRequest, isLoading };
};

export default useApiCall;
